import {
  calculateProCommission,
  calculateSalesLineBenefit,
  type SalesBenefitLineInput,
} from '@erp/domain/sales';
import { calculateProLineTotals, round3 } from './pricing';
import type { ProCartLine, ProCartTotals } from './types';

/** Cart line → entrée bénéfice (source unique @erp/domain/sales). La remise du
 *  palier ne vit dans `unitHt` QUE pour les prix fixes prix_pro_X ; pour un
 *  article tarifé en remise-%, resolveTierLinePricing garde `unitHt` au prix de
 *  base et met la remise dans `remisePct` — on la passe donc au calcul du
 *  bénéfice. `purchasePrice` est le coût net HT snapshotté. */
function toBenefitInput(
  line: Pick<ProCartLine, 'quantity' | 'unitHt' | 'remisePct' | 'purchasePrice'>,
): SalesBenefitLineInput {
  return {
    quantity: line.quantity,
    unitPriceHt: line.unitHt,
    discountPct: line.remisePct,
    unitCostHt: line.purchasePrice,
  };
}

/**
 * Commission d'UNE ligne de panier : `taux% × bénéfice ligne`, arrondie au
 * millime pour l'AFFICHAGE par-ligne (panier / validation). Marge nulle,
 * négative ou coût inconnu → 0 (on ne surévalue jamais).
 *
 * NB : le TOTAL commission d'un panier n'est PAS la somme de ces valeurs
 * arrondies — il passe par `calculateProCommission` (somme des bruts, un seul
 * arrondi final), la même fonction que le serveur recalcule à la livraison,
 * pour que le montant promis au checkout == le montant crédité ensuite.
 */
export function calculateLineCommission(
  line: Pick<ProCartLine, 'quantity' | 'unitHt' | 'remisePct' | 'purchasePrice'>,
  commissionRatePct: number,
): number {
  if (!(commissionRatePct > 0)) return 0;
  const { benefit } = calculateSalesLineBenefit(toBenefitInput(line));
  return benefit > 0 ? round3((benefit * commissionRatePct) / 100) : 0;
}

/**
 * Aggregate totals for a cart, given the per-line snapshots (unitHt,
 * remisePct, vatRate already resolved to the chosen tier).
 *
 * Commission is paid on the margin: (unitHt - purchasePrice) * qty,
 * after tier discount, before VAT. Multiplied by the pro's commission
 * rate (passed in from the user profile, default 0).
 */
export function calculateCartTotals(
  lines: ReadonlyArray<ProCartLine>,
  commissionRatePct = 0,
): ProCartTotals {
  let pht = 0;
  let pttc = 0;
  let tva = 0;
  let totalRemise = 0;
  let itemCount = 0;

  for (const line of lines) {
    const t = calculateProLineTotals({
      quantity: line.quantity,
      unitHt: line.unitHt,
      remisePct: line.remisePct,
      vatRate: line.vatRate,
    });
    pht = round3(pht + t.pht);
    pttc = round3(pttc + t.pttc);
    tva = round3(tva + t.tvaAmount);
    totalRemise = round3(totalRemise + t.montantRemise);
    itemCount += line.quantity;
  }

  // Total commission via la MÊME fonction que le serveur (`calculateProCommission`,
  // somme des bruts puis un seul arrondi) — jamais la somme des commissions
  // par-ligne déjà arrondies, sinon le montant affiché/enregistré au checkout
  // divergerait du recalcul serveur à la livraison (qui écrase la valeur).
  const totalCommission = calculateProCommission(
    lines.map(toBenefitInput),
    commissionRatePct,
  );

  return { pht, pttc, tva, totalRemise, totalCommission, itemCount };
}
